if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }

        this.formId = this.dataset.formId || this.closest('form')?.id;
        this.form = this.formId ? document.getElementById(this.formId) : this.closest('form');
        this.primarySubmitButton = this.form?.querySelector('.product-form__submit');
        this.templateButton = this.querySelector('.sf-sticky-buy-bar__button');
        this.defaultLabel = this.dataset.defaultLabel || 'Buy it now';
        this.mainContent = document.getElementById('MainContent');

        if (!this.form || !this.formId || !this.primarySubmitButton || !this.templateButton) {
          this.remove();
          return;
        }

        this.mountPortal();

        this.boundSyncState = this.syncState.bind(this);
        this.boundScheduleSync = () => window.requestAnimationFrame(this.boundSyncState);
        this.boundHandleOverlayState = this.handleOverlayState.bind(this);
        this.boundScheduleOverlayState = () => window.requestAnimationFrame(this.boundHandleOverlayState);
        this.boundCartTriggerClick = this.handleCartTriggerClick.bind(this);

        this.form.addEventListener('change', this.boundScheduleSync);
        this.form.addEventListener('input', this.boundScheduleSync);
        window.addEventListener('resize', this.boundScheduleSync, { passive: true });
        document.addEventListener('click', this.boundScheduleOverlayState, true);
        document.addEventListener('keydown', this.boundScheduleOverlayState, true);
        this.bindCartTriggers();

        this.primarySubmitObserver = new MutationObserver(() => this.boundScheduleSync());
        this.primarySubmitObserver.observe(this.primarySubmitButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        this.overlayObserver = new MutationObserver(() => this.boundScheduleOverlayState());
        this.overlayObserver.observe(document.body, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-expanded'],
        });

        this.updateOffset();
        this.syncState();
        this.handleOverlayState();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('change', this.boundScheduleSync);
        this.form?.removeEventListener('input', this.boundScheduleSync);
        window.removeEventListener('resize', this.boundScheduleSync);
        document.removeEventListener('click', this.boundScheduleOverlayState, true);
        document.removeEventListener('keydown', this.boundScheduleOverlayState, true);
        this.unbindCartTriggers();
        this.primarySubmitObserver?.disconnect();
        this.overlayObserver?.disconnect();
        if (this.overlayPollTimer) {
          window.clearInterval(this.overlayPollTimer);
          this.overlayPollTimer = null;
        }
        this.portal?.remove();
        if (this.mainContent) {
          this.mainContent.style.paddingBottom = '';
        }
      }

      syncState() {
        if (!this.portalButton || !this.form?.isConnected) {
          this.portal?.remove();
          return;
        }

        const primaryLabel = this.primarySubmitButton.querySelector('span')?.textContent?.trim();
        const variantId = this.form.querySelector('input[name="id"]')?.value;
        const isDisabled =
          !variantId ||
          this.primarySubmitButton.hasAttribute('disabled') ||
          this.primarySubmitButton.getAttribute('aria-disabled') === 'true';

        this.portalButton.disabled = isDisabled;

        if (isDisabled) {
          this.portalButton.setAttribute('aria-disabled', 'true');
          this.portalButton.querySelector('span').textContent = primaryLabel || this.defaultLabel;
        } else {
          this.portalButton.removeAttribute('aria-disabled');
          this.portalButton.querySelector('span').textContent = this.defaultLabel;
        }

        this.updateOffset();
      }

      handleOverlayState() {
        if (!this.portal) return;

        const shouldHide = this.isCartOrOverlayOpen();
        this.portal.toggleAttribute('hidden', shouldHide);
        this.portal.classList.toggle('sf-sticky-buy-bar-portal--hidden', shouldHide);
        this.updateOffset();

        if (!shouldHide) {
          this.cartTriggerActive = false;
          this.cartTriggerMisses = 0;
          if (this.overlayPollTimer) {
            window.clearInterval(this.overlayPollTimer);
            this.overlayPollTimer = null;
          }
        }
      }

      isCartOrOverlayOpen() {
        if (this.cartTriggerActive && Date.now() - (this.cartTriggerTimestamp || 0) < 900) {
          return true;
        }

        const bodyMarkerText = [
          document.body?.className || '',
          document.documentElement?.className || '',
        ]
          .join(' ')
          .toLowerCase();

        if (/\b(essential|cart|drawer)[-_ ]?(open|active|visible|shown)\b/.test(bodyMarkerText)) {
          return true;
        }

        const nativeCartDrawer =
          document.querySelector('cart-drawer.active') ||
          document.querySelector('#CartDrawer.active') ||
          document.querySelector('cart-drawer.animate.active');

        if (nativeCartDrawer && this.isElementVisible(nativeCartDrawer)) {
          return true;
        }

        if (this.hasScrollLock()) {
          return true;
        }

        const cartUiSelectors = [
          'essential-cart-drawer',
          'essential-cart',
          '[data-app*="essential-cart"]',
          '[class*="essential-cart"]',
          '[id*="essential-cart"]',
          '[class*="essential"][class*="cart"]',
          '[id*="essential"][id*="cart"]',
          '[class*="es-cart"]',
          '[id*="es-cart"]',
          '[class*="cart-drawer"]',
          '[id*="cart-drawer"]',
          '[role="dialog"][class*="cart"]',
          '[role="dialog"][id*="cart"]',
          '[aria-modal="true"][class*="cart"]',
          '[aria-modal="true"][id*="cart"]',
          '[class*="drawer"][class*="cart"]',
          '[id*="drawer"][id*="cart"]',
        ];

        const visibleCartUi = Array.from(document.querySelectorAll(cartUiSelectors.join(','))).some((element) => {
          if (element === this.portal || element.contains(this.portal) || this.portal.contains(element)) {
            return false;
          }

          if (element.closest('quick-add-modal')) {
            return false;
          }

          return this.isElementVisible(element) && this.looksLikeOpenCartUi(element);
        });

        if (visibleCartUi) {
          return true;
        }

        if (this.cartTriggerActive) {
          return this.hasLargeOverlayAfterCartTrigger();
        }

        return false;
      }

      looksLikeOpenCartUi(element) {
        const markerText = [
          element.tagName || '',
          element.id || '',
          element.className || '',
          element.getAttribute('data-app') || '',
          element.getAttribute('data-testid') || '',
          element.getAttribute('aria-label') || '',
          element.getAttribute('aria-labelledby') || '',
        ]
          .join(' ')
          .toLowerCase();

        const hasOpenState =
          element.hasAttribute('open') ||
          element.getAttribute('aria-hidden') === 'false' ||
          element.getAttribute('aria-modal') === 'true' ||
          /\b(active|open|opened|visible|show|shown)\b/.test(markerText);

        const looksCartRelated = /\b(cart|drawer|essential)\b/.test(markerText);

        return hasOpenState || looksCartRelated;
      }

      hasScrollLock() {
        const bodyStyle = window.getComputedStyle(document.body);
        const rootStyle = window.getComputedStyle(document.documentElement);

        return (
          bodyStyle.overflow === 'hidden' ||
          bodyStyle.overflowY === 'hidden' ||
          rootStyle.overflow === 'hidden' ||
          rootStyle.overflowY === 'hidden' ||
          document.body.classList.contains('overflow-hidden') ||
          document.documentElement.classList.contains('overflow-hidden')
        );
      }

      hasLargeOverlayAfterCartTrigger() {
        const viewportWidth = window.innerWidth || 0;
        const viewportHeight = window.innerHeight || 0;
        const overlayCandidates = document.querySelectorAll(
          'iframe, dialog, [role="dialog"], [aria-modal="true"], [class*="drawer"], [id*="drawer"], [class*="cart"], [id*="cart"], [class*="essential"], [id*="essential"], body > div, body > section, body > aside'
        );

        return Array.from(overlayCandidates).some((element) => {
          if (!element || element === this.portal || element.contains(this.portal) || this.portal.contains(element)) {
            return false;
          }

          if (!this.isElementVisible(element)) {
            return false;
          }

          const style = window.getComputedStyle(element);
          const rect = element.getBoundingClientRect();
          const markerText = [
            element.tagName || '',
            element.id || '',
            element.className || '',
            element.getAttribute('data-app') || '',
            element.getAttribute('data-testid') || '',
            element.getAttribute('aria-label') || '',
            element.innerText || '',
          ]
            .join(' ')
            .toLowerCase();

          const isOverlayLike =
            ['fixed', 'absolute', 'sticky'].includes(style.position) ||
            element.tagName === 'DIALOG' ||
            element.getAttribute('role') === 'dialog' ||
            element.getAttribute('aria-modal') === 'true';

          const isLargeEnough =
            rect.width >= viewportWidth * 0.45 &&
            rect.height >= viewportHeight * 0.2;

          const mentionsCartUi =
            /\b(cart|drawer|essential|subtotal|checkout|discount|savings|rewards|panier|carrito)\b/.test(markerText);

          return isOverlayLike && isLargeEnough && mentionsCartUi;
        });
      }

      isElementVisible(element) {
        if (!element?.isConnected) return false;

        const style = window.getComputedStyle(element);
        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          style.visibility === 'collapse' ||
          parseFloat(style.opacity || '1') === 0
        ) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }

      bindCartTriggers() {
        this.cartTriggers = Array.from(
          document.querySelectorAll('#cart-icon-bubble, .header__icon--cart, a[href*="/cart"]')
        );

        this.cartTriggers.forEach((trigger) => {
          trigger.addEventListener('click', this.boundCartTriggerClick, true);
        });
      }

      unbindCartTriggers() {
        this.cartTriggers?.forEach((trigger) => {
          trigger.removeEventListener('click', this.boundCartTriggerClick, true);
        });
      }

      handleCartTriggerClick() {
        this.cartTriggerActive = true;
        this.cartTriggerTimestamp = Date.now();
        this.cartTriggerMisses = 0;
        this.handleOverlayState();

        if (this.overlayPollTimer) {
          window.clearInterval(this.overlayPollTimer);
        }

        this.overlayPollTimer = window.setInterval(() => {
          const overlayOpen = this.isCartOrOverlayOpen();

          if (overlayOpen) {
            this.cartTriggerMisses = 0;
          } else {
            this.cartTriggerMisses = (this.cartTriggerMisses || 0) + 1;
          }

          if (this.cartTriggerMisses >= 3) {
            this.cartTriggerActive = false;
            this.handleOverlayState();
          } else {
            this.portal?.toggleAttribute('hidden', overlayOpen);
            this.portal?.classList.toggle('sf-sticky-buy-bar-portal--hidden', overlayOpen);
            this.updateOffset();
          }
        }, 250);
      }

      updateOffset() {
        if (!this.portal) return;

        if (this.portal.hidden || this.portal.classList.contains('sf-sticky-buy-bar-portal--hidden')) {
          if (this.mainContent) {
            this.mainContent.style.paddingBottom = '';
          }
          return;
        }

        const height = Math.ceil(this.portal.getBoundingClientRect().height || 0);
        if (this.mainContent) {
          this.mainContent.style.paddingBottom = `${height + 16}px`;
        }
      }

      mountPortal() {
        const existingPortal = document.querySelector(
          `.sf-sticky-buy-bar-portal[data-form-id="${this.formId}"]`
        );
        if (existingPortal) {
          existingPortal.remove();
        }

        this.portal = document.createElement('div');
        this.portal.className = 'sf-sticky-buy-bar-portal';
        this.portal.dataset.formId = this.formId;

        this.portalButton = this.templateButton.cloneNode(true);
        this.portalButton.setAttribute('form', this.formId);
        this.portal.appendChild(this.portalButton);
        document.body.appendChild(this.portal);
      }
    }
  );
}
